/**
 * `nameFloatMap.ts` — helpers for the
 * `Dictionary<string, float>` shape Unity uses for transforms in
 * `EntitySnapshotData` / `PartData` / etc.
 *
 * Wire shape (Dynamo): `M` (Map) attribute with `N` (Number) values
 * keyed by axis names. After `unmarshall(...)`:
 *
 *   { x: 1.2, y: 3.4, z: 5.6 }
 *
 * Reference: `AWSDynamoHelpers.SafeGetVectorAttribute` —
 *   `foreach (string attrkey in dict[key].M.Keys) retval.Add(attrkey, float.Parse(M[attrkey].N));`
 *
 * Defensive notes:
 *   - Unity also serializes `NameFloatPair[]` as fallback. If the
 *     marshalled wire is a list of `{Name, Value}` objects we fold it
 *     into the same map shape transparently.
 *   - Missing keys default to `defaults` (typically `[0,0,0]` for
 *     position/rotation, `[1,1,1]` for scale).
 */

import type { Vec3 } from "./entityData";

export type NameFloatMap = Record<string, number>;

type RawNameFloatPair = {
  Name?: string;
  name?: string;
  Value?: number | string;
  value?: number | string;
  Val?: number | string;
  val?: number | string;
};

/**
 * Coerce whatever Dynamo handed back into a `NameFloatMap`. Accepts:
 *   - already-decoded `Record<string, number>` (the common case)
 *   - `NameFloatPair[]` shape (`[{Name:'x', Value:1}, ...]`)
 *   - `null` / `undefined`
 */
export function toNameFloatMap(value: unknown): NameFloatMap {
  if (!value) return {};
  if (Array.isArray(value)) {
    const out: NameFloatMap = {};
    for (const pair of value as RawNameFloatPair[]) {
      const k = pair?.Name ?? pair?.name;
      const v = coerceNumber(pair?.Value ?? pair?.value ?? pair?.Val ?? pair?.val);
      if (typeof k === "string" && v !== null) out[k] = v;
    }
    return out;
  }
  if (typeof value === "object") {
    const out: NameFloatMap = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const n = coerceNumber(v);
      if (n !== null) out[k] = n;
    }
    return out;
  }
  return {};
}

/**
 * Read `[x, y, z]` from a `NameFloatMap`, applying defaults for any
 * missing axis. Mirrors `SafeGetVector3Attribute(... defaultValue ...)`
 * in `AWSDynamoHelpers.cs`.
 */
export function vec3From(map: NameFloatMap, defaults: Vec3): Vec3 {
  const x = typeof map.x === "number" ? map.x : defaults[0];
  const y = typeof map.y === "number" ? map.y : defaults[1];
  const z = typeof map.z === "number" ? map.z : defaults[2];
  return [x, y, z];
}

/** Inverse of {@link vec3From} — the `{x, y, z}` shape Dynamo stores as an `M`. */
export function vecToNameFloatMap(v: Vec3): NameFloatMap {
  return { x: v[0], y: v[1], z: v[2] };
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}
